// src/services/restaurantService.js
// 餐廳訂位系統 API 服務層 — Owned Reservation v1 (feature_plans/082)
//
// 後端回傳形狀 (controllers/{restaurant-tables,reservation}.controller.js):
//   { success: true, data: { ... } }   或
//   { success: false, error: '...' }
// 此 service 統一回傳 response.data（拆掉 axios envelope），讓 page 直接用 body.data.

import apiService from './apiService';

const unwrap = async (promise) => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    const body = error?.response?.data;
    if (body && body.success === false) return body;
    return {
      success: false,
      error: error?.response?.data?.error || error?.message || '請求失敗'
    };
  }
};

// ──────────────────────────────────────────────────────────────
// 桌位 (/api/restaurant/tables)
// ──────────────────────────────────────────────────────────────

const getTables = () =>
  unwrap(apiService.apiRequest('get', 'restaurant/tables'));

const createTable = (payload) =>
  unwrap(apiService.apiRequest('post', 'restaurant/tables', payload));

const updateTable = (id, payload) =>
  unwrap(apiService.apiRequest('patch', `restaurant/tables/${id}`, payload));

const toggleTable = (id) =>
  unwrap(apiService.apiRequest('patch', `restaurant/tables/${id}/toggle`));

const deleteTable = (id) =>
  unwrap(apiService.apiRequest('delete', `restaurant/tables/${id}`));

// ──────────────────────────────────────────────────────────────
// 訂位 (/api/reservations)
// ──────────────────────────────────────────────────────────────

const getReservations = (params = {}) => {
  const q = new URLSearchParams();
  if (params.date) q.append('date', params.date);
  if (params.status) q.append('status', params.status);
  if (params.source) q.append('source', params.source);
  const qs = q.toString();
  return unwrap(apiService.apiRequest('get', `reservations${qs ? `?${qs}` : ''}`));
};

// 119 營運總覽「新進訂單」：最近建立的訂位，跨日期、依 created_at 由新到舊。
// 回傳 { success, data: { reservations, limit } }。
const getRecentReservations = (limit = 30) =>
  unwrap(apiService.apiRequest('get', `reservations/recent?limit=${encodeURIComponent(limit)}`));

const createReservation = (payload) =>
  unwrap(apiService.apiRequest('post', 'reservations', payload));

const updateReservation = (id, patch) =>
  unwrap(apiService.apiRequest('patch', `reservations/${id}`, patch));

const updateReservationStatus = (id, status) =>
  unwrap(apiService.apiRequest('patch', `reservations/${id}/status`, { status }));

const reassignTable = (id, tableOrIds) =>
  unwrap(apiService.apiRequest('patch', `reservations/${id}/reassign`,
    Array.isArray(tableOrIds) ? { tableIds: tableOrIds } : { tableId: tableOrIds }));

// T-023 自選桌號：某日期+時段全部桌的可用狀態（已佔/坐得下），給清單式 picker。
const getTableAvailability = (params = {}) => {
  const q = new URLSearchParams();
  if (params.date) q.append('date', params.date);
  if (params.time) q.append('time', params.time);
  if (params.partySize != null) q.append('partySize', params.partySize);
  if (params.excludeReservationId) q.append('excludeReservationId', params.excludeReservationId);
  return unwrap(apiService.apiRequest('get', `reservations/table-availability?${q.toString()}`));
};

const cancelReservation = (id) =>
  unwrap(apiService.apiRequest('delete', `reservations/${id}`));

// ──────────────────────────────────────────────────────────────
// 客滿（訂位額滿）(/api/restaurant/soldout) — 一日至多一筆；web + AI 電話同步停收
// ──────────────────────────────────────────────────────────────

// 列出所有客滿 → { success, items:[{id,date,meal:'all'|'lunch'|'dinner'}] }
const getSoldout = () =>
  unwrap(apiService.apiRequest('get', 'restaurant/soldout'));

// 設定/覆蓋某日客滿（meal: 'all'|'lunch'|'dinner'）
const setSoldout = (date, meal) =>
  unwrap(apiService.apiRequest('post', 'restaurant/soldout', { date, meal }));

// 清除某日客滿（恢復開放）
const clearSoldout = (date) =>
  unwrap(apiService.apiRequest('delete', `restaurant/soldout/${date}`));

// ──────────────────────────────────────────────────────────────
// 本日狀態：客滿 + 店休 (/api/restaurant/day-status) — Plan 117
//
// 是上面 soldout 端點的超集：多了「店休」，也多了「這一天是誰設的、能不能在這裡改」。
// soldout 端點保留給尚未改用本端點的地方，不要在同一個畫面混用兩者。
//
// 回傳 items[]：
//   { date, status:'soldout'|'closure', meal:'all'|'lunch'|'dinner',
//     source:'day'|'range'|'weekly', sourceId, sourceName, editableHere, label, messageText }
//
// source='range'|'weekly'（來自線上訂位設定的區間／固定公休）→ editableHere=false，
// 在此設定或清除會拿到 409 { error:'CONFLICT' } —— 呼叫端要顯示「請至線上訂位設定調整」，
// 不要當成一般錯誤。
// ──────────────────────────────────────────────────────────────

const getDayStatus = (from, to) => {
  const q = new URLSearchParams();
  if (from) q.append('from', from);
  if (to) q.append('to', to);
  const qs = q.toString();
  return unwrap(apiService.apiRequest('get', `restaurant/day-status${qs ? `?${qs}` : ''}`));
};

// status: 'soldout' | 'closure'；meal: 'all' | 'lunch' | 'dinner'
const setDayStatus = (date, status, meal) =>
  unwrap(apiService.apiRequest('post', 'restaurant/day-status', { date, status, meal }));

const clearDayStatus = (date) =>
  unwrap(apiService.apiRequest('delete', `restaurant/day-status/${date}`));

// ──────────────────────────────────────────────────────────────
// 未讀新訂位徽章 (/api/reservations/unseen-count) — T-031
// ──────────────────────────────────────────────────────────────
// 回自 since(ISO) 之後新建立且未取消的訂位數 → { success, data: { count } }
const getUnseenReservationCount = (since) =>
  unwrap(apiService.apiRequest(
    'get',
    `reservations/unseen-count${since ? `?since=${encodeURIComponent(since)}` : ''}`
  ));

const restaurantService = {
  getTables,
  createTable,
  updateTable,
  toggleTable,
  deleteTable,
  getReservations,
  getRecentReservations, // 119 新進訂單
  createReservation,
  updateReservation,
  updateReservationStatus,
  reassignTable,
  getTableAvailability,
  cancelReservation,
  getSoldout,
  setSoldout,
  clearSoldout,
  getDayStatus,
  setDayStatus,
  clearDayStatus,
  getUnseenReservationCount,
};

export default restaurantService;
